import { useEffect, useRef, useState } from 'react';
import styled from '@/styles';
import { DependabilityV2 } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import ScoreRing from './ScoreRing';
import TierPill from './TierPill';
import TierPopover from './TierPopover';
import StatusBadgeRow from './StatusBadgeRow';
import OnboardingChips from './OnboardingChips';
import StatCards from './StatCards';
import ActivityFeed from './ActivityFeed';
import LocationFlagModal from './LocationFlagModal';
import useLocationFlags from './useLocationFlags';
import ScoreHistoryModal from './ScoreHistoryModal';
import ActivationBreakdownModal from './ActivationBreakdownModal';
import RecentActivityModal from './RecentActivityModal';
import useDependabilityEvents from './useDependabilityEvents';
import {
  trackDependabilityBreakdownOpened,
  trackDependabilityFeedExpanded,
  trackDependabilityTierInfoOpened,
} from '@/util/vexo';

// Deliberately NOT elements/Card: the 2.0-Baseline card style is flat and
// outlined, which would mean negating Card's shadow and radius tokens.
const Wrapper = styled('div', {
  width: '100%',
  backgroundColor: 'white',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  borderRadius: '12px',
  border: `1px solid ${dependabilityV2Colors.depBorder}`,
});
const TopSection = styled('div', {
  display: 'flex',
  gap: '32px',
  // ring column is vertically centered against the taller right column
  alignItems: 'center',
  flexWrap: 'wrap',
  paddingBottom: '24px',
  borderBottom: `1px solid ${dependabilityV2Colors.depBorder}`,
});
const ScoreColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
});
const RightColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  flex: '1 1 320px',
  minWidth: 0,
});
// The badges stand alone in the right column now that the tier pill has moved
// under the score ring. The group keeps its testid so the tier popover's
// collision heuristic (which treats the badges as an obstacle) and the specs can
// still find it; StatusBadgeRow's own Row handles wrapping.
const BadgeGroup = styled('div', {
  flex: '1 1 auto',
  minWidth: 0,
  // Stack the accountability badge row and (for activation workers) the
  // onboarding chips vertically, badges on top.
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  alignItems: 'flex-start',
});
const Breakdown = styled('button', {
  background: 'none',
  border: 'none',
  padding: 0,
  textAlign: 'center',
  color: dependabilityV2Colors.depPositive,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '$sm',
  textDecoration: 'underline',
});

const FEED_PAGE_SIZE = 3;

interface Props {
  workerId: number;
  data: DependabilityV2;
}

const DependabilityV2Card = ({ workerId, data }: Props) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  // GRAVY-10365. State lives here rather than in StatusBadgeRow so the badge stays a
  // presentational chip and the modal mounts as a sibling of the other two dialogs.
  const [locationFlagsOpen, setLocationFlagsOpen] = useState(false);
  // GRAVY-10347: the tier popover measures this wrapper to place itself clamped
  // inside the card (never over the header or under the sidebar).
  const tierAnchorRef = useRef<HTMLSpanElement>(null);

  // The tier popover is HOVER-driven: it shows while the pointer (or keyboard
  // focus) is on the pill or the popover, and hides shortly after both are left.
  // The popover is a child of this anchor, so hovering it still counts as
  // hovering the anchor (mouseleave doesn't fire when moving to a descendant).
  // The only bare spot is the small physical gap between the pill and the
  // floating popover — a short close delay bridges it so moving the pointer
  // across the gap to read the popover doesn't flicker it shut. A click still
  // opens it too (unchanged), which is the fallback for touch devices with no
  // hover.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Fires the "tier info opened" analytics once per open cycle. It moved here
  // from TierPill's onClick: hover/focus open the popover, so `expanded` was
  // already true by the time a click landed and the old click-gated call never
  // fired. The ref dedupes repeat triggers within one open (hover + focus +
  // click) and resets when the popover actually closes, so a genuine reopen
  // fires again.
  const tierInfoTrackedRef = useRef(false);
  const openPopover = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (!tierInfoTrackedRef.current) {
      tierInfoTrackedRef.current = true;
      trackDependabilityTierInfoOpened({
        worker_id: workerId,
        tier: data.tier,
      });
    }
    setPopoverOpen(true);
  };
  const closePopoverSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setPopoverOpen(false);
      tierInfoTrackedRef.current = false;
    }, 150);
  };
  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  // The inline feed shows only the first page; "View more" opens the
  // activity modal (view-activities frame) rather than growing the card.
  const { events, loading, loaded, error, loadPage, hasMore } =
    useDependabilityEvents(workerId, FEED_PAGE_SIZE);

  // GRAVY-10365. Loaded with the card, not on modal open: the badge needs the
  // count up front, and sharing one hook with the modal is what guarantees the
  // chip and the modal header agree.
  const locationFlags = useLocationFlags(workerId);

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  useEffect(() => {
    locationFlags.load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId]);

  // GRAVY-10429: activation workers get the dedicated Activation breakdown
  // modal; reliability workers keep ScoreHistoryModal. Guarded on the payload
  // block being present too, so a rolling-deploy backend that reports
  // scoring_mode='activation' without the new block still falls back to the
  // reliability breakdown (which renders the activation footer copy) rather
  // than opening an empty modal.
  const showActivationBreakdown =
    data.scoring_mode === 'activation' && !!data.activation_breakdown;
  // Figma 40013-12759: an activation worker's overview card shows onboarding
  // milestone chips in place of the reliability status badges.
  const showActivationChips =
    data.scoring_mode === 'activation' &&
    !!data.activation_breakdown?.milestones?.length;

  return (
    <Wrapper data-testid="dependability-v2-card">
      <TopSection>
        <ScoreColumn data-testid="dependability-score-col">
          <ScoreRing score={data.score} tier={data.tier} />
          {/* The tier badge sits directly under the score ring, above the
              See-breakdown link. The popover is anchored to this wrapper. */}
          <span
            ref={tierAnchorRef}
            style={{ position: 'relative', display: 'inline-flex' }}
            onMouseEnter={openPopover}
            onMouseLeave={closePopoverSoon}
            onFocus={openPopover}
            onBlur={closePopoverSoon}
          >
            <TierPill
              tier={data.tier}
              label={data.tier_label}
              onInfoClick={openPopover}
              expanded={popoverOpen}
              accountabilityStatus={data.accountability_status}
              isSuspended={data.is_suspended}
              scoringMode={data.scoring_mode}
            />
            <TierPopover
              isOpen={popoverOpen}
              onRequestClose={() => setPopoverOpen(false)}
              anchorRef={tierAnchorRef}
              // fallback static side when no anchor is measured (unit tests)
              justify="right"
              boundaries={data.tier_boundaries}
              score={data.score}
              tier={data.tier}
              completedShifts={data.completed_shifts_count}
              scoringMode={data.scoring_mode}
              // New (activation) workers open a compact "Joined <date>" tooltip
              // instead of the tier bar (Figma 40013-12830). Absent when the
              // activation_breakdown block hasn't deployed → tier-view fallback.
              newWorkerJoinedAt={
                data.scoring_mode === 'activation'
                  ? data.activation_breakdown?.account_created_at
                  : undefined
              }
            />
          </span>
          <Breakdown
            type="button"
            onClick={() => {
              trackDependabilityBreakdownOpened({
                worker_id: workerId,
                kind: showActivationBreakdown ? 'activation' : 'reliability',
                tier: data.tier,
              });
              setHistoryOpen(true);
            }}
          >
            See breakdown
          </Breakdown>
        </ScoreColumn>
        <RightColumn>
          {/* Accountability badges (no-shows, late, late-cancels, preferred
              pools) always render — an activation worker (0–4 shifts) can still
              have all-time no-shows/late-drops an admin needs to see. When the
              worker also has onboarding milestones, the chips render BELOW the
              badges (badges first). */}
          <BadgeGroup data-testid="dependability-badge-group">
            {/* GRAVY-10452: the badge takes TOTAL, not unresolved. It and the
                modal header it opens must be the same number — they read 3
                and 4 one click apart — and the header counts the list below
                it, which deliberately includes resolved entries so ops can
                revisit something already approved. Moving the header to
                unresolved instead would have left it undercounting its own
                rows. */}
            <StatusBadgeRow
              badges={data.badges}
              locationFlagCount={locationFlags.totalCount}
              onLocationFlagsClick={() => setLocationFlagsOpen(true)}
            />
            {showActivationChips && (
              <OnboardingChips
                milestones={data.activation_breakdown?.milestones ?? []}
              />
            )}
          </BadgeGroup>
          <StatCards stats={data.stats} />
        </RightColumn>
      </TopSection>
      <ActivityFeed
        events={events}
        hasMore={hasMore}
        loading={loading || !loaded}
        error={error}
        onRetry={() => loadPage(1)}
        onViewMore={() => {
          trackDependabilityFeedExpanded({ worker_id: workerId, page: 1 });
          setActivityOpen(true);
        }}
      />
      <RecentActivityModal
        workerId={workerId}
        isOpen={activityOpen}
        onClose={() => setActivityOpen(false)}
      />
      <LocationFlagModal
        isOpen={locationFlagsOpen}
        onClose={() => setLocationFlagsOpen(false)}
        flags={locationFlags.flags}
        totalCount={locationFlags.totalCount}
        windowDays={locationFlags.windowDays}
        loading={locationFlags.loading}
        loaded={locationFlags.loaded}
        error={locationFlags.error}
        retry={locationFlags.retry}
      />
      {showActivationBreakdown ? (
        <ActivationBreakdownModal
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          breakdown={data.activation_breakdown!}
        />
      ) : (
        <ScoreHistoryModal
          workerId={workerId}
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          signals={data.signals}
          completedShifts={data.completed_shifts_count}
          scoringMode={data.scoring_mode}
        />
      )}
    </Wrapper>
  );
};

export default DependabilityV2Card;
